import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';

export function RemixCommentPrimaryMenuItem({
	api,
	isDisabled,
	remixDisabledReason,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}): React.JSX.Element {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isRemixing, areAiEntryPointsDisabled } = useSharedPluginStateWithSelector(
		api,
		['malleableUi', 'aiExperience'],
		(states) => ({
			isRemixing: states.malleableUiState?.isRemixModalOpen || states.malleableUiState?.isThinking,
			areAiEntryPointsDisabled: fg('platform_editor_ai_toggle_entry_points')
				? (states.aiExperienceState?.areAiEntryPointsDisabled ?? false)
				: false,
		}),
	);

	const effectiveDisabled = isDisabled || areAiEntryPointsDisabled;

	const handleClick = useCallback(() => {
		if (effectiveDisabled || isRemixing) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'remixModalFromToolbar',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				attributes: {
					entryPoint: 'editor',
				},
			},
		}).fire();
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'commentPrimaryToolbar',
			},
		});
		api?.core.actions.execute(
			api?.malleableUi?.commands.openRemixModal({
				metadata: { interactionSource: 'remixModalFromToolbar' },
				source: 'commentPrimaryToolbar',
			}),
		);
	}, [api, effectiveDisabled, isRemixing, experienceTracker, createAnalyticsEvent]);

	const label = formatMessage(messages.remixToolbarLabel);

	const tooltipContent = useMemo(() => {
		if (isRemixing) {
			return formatMessage(messages.remixInProgressTooltip);
		}
		if (effectiveDisabled) {
			return getRemixDisabledTooltip(formatMessage, remixDisabledReason);
		}
		return formatMessage(messages.remixToolbarTooltip);
	}, [isRemixing, effectiveDisabled, formatMessage, remixDisabledReason]);

	return (
		<ToolbarDropdownItem
			elemBefore={<AiGenerativeRemixIcon size="small" label={label} />}
			elemAfter={<div />}
			testId="dropdown-item__malleable-ui-remix-comment"
			isDisabled={effectiveDisabled || isRemixing}
			title={tooltipContent}
			onClick={handleClick}
		>
			{label}
		</ToolbarDropdownItem>
	);
}
